import { router } from '@inertiajs/react';
import { Loader2, LogIn, LogOut, UserRoundCog } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/Components/ui/dialog';

type LocalDevUser = {
    alias: string;
    label: string;
    email: string;
    role: string;
    exists: boolean;
};

type LocalDevLoginPayload = {
    enabled: boolean;
    users: Record<string, LocalDevUser>;
};

type PageProps = {
    auth?: {
        user?: {
            email?: string | null;
        } | null;
    };
    localDevLogin?: LocalDevLoginPayload;
};

export default function LocalDevAccountSwitcher({ initialPageProps }: { initialPageProps: PageProps }) {
    const [pageProps, setPageProps] = useState<PageProps>(initialPageProps);
    const payload = pageProps.localDevLogin;
    const [open, setOpen] = useState(false);
    const [processingAlias, setProcessingAlias] = useState<string | null>(null);
    const [loggingOut, setLoggingOut] = useState(false);

    const users = useMemo(() => Object.values(payload?.users ?? {}), [payload?.users]);
    const currentEmail = pageProps.auth?.user?.email ?? null;

    useEffect(() => {
        const unsubscribe = router.on('navigate', (event: any) => {
            setPageProps((event?.detail?.page?.props ?? {}) as PageProps);
        });

        return () => unsubscribe();
    }, []);

    if (!payload?.enabled) {
        return null;
    }

    const login = (alias: string) => {
        setProcessingAlias(alias);

        router.post(`/_local/login/${alias}`, {}, {
            preserveScroll: true,
            onSuccess: () => setOpen(false),
            onFinish: () => setProcessingAlias(null),
        });
    };

    const logout = () => {
        setLoggingOut(true);

        router.post('/_local/logout', {}, {
            preserveScroll: true,
            onSuccess: () => setOpen(false),
            onFinish: () => setLoggingOut(false),
        });
    };

    return (
        <Dialog open={open} onOpenChange={(nextOpen) => {
            if (!processingAlias && !loggingOut) {
                setOpen(nextOpen);
            }
        }}>
            <DialogTrigger asChild>
                <button
                    type="button"
                    data-testid="local-dev-account-switcher-trigger"
                    aria-label="Переключить local аккаунт"
                    title="Переключить local аккаунт"
                    className="fixed bottom-5 right-5 z-[70] inline-flex h-12 w-12 items-center justify-center rounded-full border border-slate-700 bg-slate-950 text-white shadow-xl shadow-slate-950/25 transition hover:-translate-y-0.5 hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                    <UserRoundCog className="h-5 w-5" />
                </button>
            </DialogTrigger>

            <DialogContent
                data-testid="local-dev-account-switcher"
                className="bottom-20 left-auto right-4 top-auto max-h-[calc(100vh-7rem)] w-[calc(100vw-2rem)] max-w-sm translate-x-0 translate-y-0 overflow-hidden p-0 sm:bottom-24 sm:right-5"
            >
                <div className="border-b border-slate-200 bg-white px-4 py-4">
                    <DialogHeader className="space-y-1 text-left">
                        <DialogTitle className="flex items-center gap-2 text-base">
                            <UserRoundCog className="h-4 w-4" />
                            Local аккаунт
                        </DialogTitle>
                        <DialogDescription>
                            Быстрый вход под ролью для проверки интерфейса.
                        </DialogDescription>
                    </DialogHeader>
                </div>

                <div className="max-h-[min(28rem,calc(100vh-14rem))] overflow-y-auto px-3 py-3">
                    <div className="space-y-2">
                        {users.map((user) => {
                            const isCurrent = user.email === currentEmail;
                            const isProcessing = processingAlias === user.alias;

                            return (
                                <div
                                    key={user.alias}
                                    className="rounded-md border border-slate-200 bg-white p-3"
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2">
                                                <p className="truncate text-sm font-semibold text-slate-950">
                                                    {user.label}
                                                </p>
                                                {isCurrent && (
                                                    <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[11px] font-medium text-emerald-700">
                                                        active
                                                    </span>
                                                )}
                                            </div>
                                            <p className="mt-1 break-all text-xs text-slate-500">
                                                {user.email}
                                            </p>
                                            {!user.exists && (
                                                <p className="mt-1 text-xs text-amber-700">
                                                    Нужен db:seed
                                                </p>
                                            )}
                                        </div>

                                        <button
                                            type="button"
                                            data-testid={`local-dev-login-${user.alias}`}
                                            onClick={() => login(user.alias)}
                                            disabled={isProcessing || !user.exists}
                                            className="inline-flex h-8 shrink-0 items-center justify-center gap-1.5 rounded-md bg-indigo-600 px-3 text-xs font-medium text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            {isProcessing ? (
                                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                            ) : (
                                                <LogIn className="h-3.5 w-3.5" />
                                            )}
                                            Войти
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="border-t border-slate-200 bg-slate-50 px-3 py-3">
                    <button
                        type="button"
                        onClick={logout}
                        disabled={loggingOut}
                        className="inline-flex h-9 w-full items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {loggingOut ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <LogOut className="h-4 w-4" />
                        )}
                        Выйти
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
